import type { ReactNode } from 'react'
import type { LinkProps, To } from 'react-router-dom'

import type { ChartData } from '../../../chartsModule/types'
import type { AnalyticsExternalSource } from '../../types'
import type { AnalyticsTooltipContentProps } from '../AnalyticsTooltipContent/AnalyticsTooltipContent'

export type AnalyticsCardType = 'lineChart' | 'barChart' | 'barStats' | 'stats'

type ChartTypeProps = {
  value: number | [number, number]
  data: ChartData[]
  formatValue?: (value: number) => ReactNode
  formatXAxis?: (value: string) => ReactNode
  formatYAxis?: (value: number) => ReactNode
  formatTooltipXAxis?: (value: string) => ReactNode
  formatTooltipYAxis?: (value: number) => ReactNode
}

export type StatsData = {
  label: ReactNode
  value: number | [number, number]
}

export type LineChartTypeProps = ChartTypeProps & {
  type: Extract<AnalyticsCardType, 'lineChart'>
}

export type BarChartTypeProps = ChartTypeProps & {
  type: Extract<AnalyticsCardType, 'barChart'>
}

export type BarStatsTypeProps = {
  type: Extract<AnalyticsCardType, 'barStats'>
  data: StatsData[]
}

export type StatsTypeProps = {
  type: Extract<AnalyticsCardType, 'stats'>
  value?: number | [number, number]
  data: StatsData[]
}

export type ReportLink = To | { to: To; state?: LinkProps['state'] }

export type AnalyticsCardHeader = {
  label: string
  tooltip?: AnalyticsTooltipContentProps
  options?: {
    label: string
    value: string
  }[]
  onOptionSelect?: (value: string) => void
}

export type AnalyticsCardProps = {
  header: AnalyticsCardHeader
  externalSource?: AnalyticsExternalSource
  reportLink?: ReportLink
  isDefinitionLoading?: boolean
  isDataLoading?: boolean
  legend?: string
  emptyMessage?: string
  formatValue?: (value: number) => ReactNode
} & (LineChartTypeProps | BarChartTypeProps | BarStatsTypeProps | StatsTypeProps)

export type AnalyticsCardLineChartProps = Extract<AnalyticsCardProps, { type: 'lineChart' }>

export type AnalyticsCardBarStatsProps = Extract<AnalyticsCardProps, { type: 'barStats' }>

export type AnalyticsCardStatsProps = Extract<AnalyticsCardProps, { type: 'stats' }>

// Type guards
export const isLineChartType = (
  props: AnalyticsCardProps,
): props is AnalyticsCardProps & LineChartTypeProps => {
  return props.type === 'lineChart'
}

export const isBarChartType = (
  props: AnalyticsCardProps,
): props is AnalyticsCardProps & BarChartTypeProps => {
  return props.type === 'barChart'
}

export const isBarStatsType = (
  props: AnalyticsCardProps,
): props is AnalyticsCardProps & BarStatsTypeProps => {
  return props.type === 'barStats'
}

export const isStatsType = (
  props: AnalyticsCardProps,
): props is AnalyticsCardProps & StatsTypeProps => {
  return props.type === 'stats'
}
